<template>
  <div class="dashboard-container">
    <header class="dashboard-header">
      <h1 v-if="headerConfig.show_titles">{{ tConfig(headerConfig.title, lang) }}</h1>
      <p v-if="headerConfig.show_titles">{{ tConfig(headerConfig.subtitle, lang) }}</p>

      <div class="tabs-container">
        <button
          v-for="screen in screens.filter(s => s.active !== false)"
          :key="screen.id"
          class="tab-button"
          :class="{ active: activeScreenId === screen.id }"
          @click="activeScreenId = screen.id"
        >
          {{ tConfig(screen.title, lang) }}
        </button>
        <button v-if="isPdfExportEnabled" class="pdf-btn" @click="openPdfExportModal" :title="t('exportPdf', lang)">📄</button>
        <button v-if="isEditable" class="config-btn" @click="$emit('enter-config', { screenId: activeScreenId })" :title="t('openConfig', lang)">⚙</button>
      </div>
    </header>

    <div class="dashboard-content">
      <div class="kpi-grid" v-if="kpiWidgets.length > 0">
        <div class="kpi-card-wrapper" v-for="widget in kpiWidgets" :key="widget.id" style="position: relative;">
          <button
            v-if="isEditable"
            class="widget-config-btn"
            @click.stop="$emit('open-widget-config', { screenId: activeScreenId, widgetId: widget.id })"
            title="Configurar widget"
          >⚙</button>
          <KpiWidget
            :title="tConfig(widget.params.title, lang)"
            :value="chartData[widget.dataSourceKey]?.[widget.params.valueKey] || 0"
          />
        </div>
      </div>

      <div v-if="apiError" class="api-error">
        <span class="api-error-icon">⚠️</span>
        <span class="api-error-msg">{{ apiError }}</span>
      </div>

      <div v-if="loading" class="loading-overlay">
        <spinner-loading :size="36" :line-size="3" line-fg-color="#d42e12" />
      </div>

      <div v-else class="grid-layout">
        <div
          v-for="widget in otherWidgets"
          :key="widget.id"
          class="widget-wrapper"
          :class="{
            'full-width': widget.width === 'full',
            'half-width': widget.width === 'half',
            'third-width': widget.width === 'third',
            'two-thirds-width': widget.width === 'two-thirds'
          }"
        >
          <button
            v-if="isEditable"
            class="widget-config-btn"
            @click.stop="$emit('open-widget-config', { screenId: activeScreenId, widgetId: widget.id })"
            title="Configurar widget"
          >⚙</button>

          <ChartWidget
            v-if="widget.type === 'chart' && widget.params.chart?.type !== 'table'"
            :options="getMergedOptions(widget)"
            :series="chartData[widget.dataSourceKey]?.series || []"
            :allow-type-change="widget.params.allowTypeChange"
            :editable="isEditable ?? true"
            :lang="lang"
            @data-point-selection="(config) => handleChartClick(widget, config)"
          />

          <!-- Table view for chart widgets -->
          <div v-if="widget.type === 'chart' && widget.params.chart?.type === 'table'" class="table-widget">
            <h3 v-if="widget.params.title?.text || widget.params.title" class="table-title">
              {{ tConfig(widget.params.title?.text || widget.params.title, lang) }}
            </h3>
            <table class="data-table">
              <thead>
                <tr>
                  <th v-if="widget.params.mapping?.category">{{ widget.params.mapping.category }}</th>
                  <th v-if="widget.params.mapping?.value">{{ widget.params.mapping.value }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in (chartData[widget.dataSourceKey]?.data || [])" :key="idx">
                  <td v-if="widget.params.mapping?.category">
                    {{ getTableCellValue(item, widget.params.mapping.category) }}
                  </td>
                  <td v-if="widget.params.mapping?.value">
                    {{ getTableCellValue(item, widget.params.mapping.value) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <MapWidget
            v-if="widget.type === 'map'"
            :data="chartData[widget.dataSourceKey]?.data || []"
            :map-key-field="widget.params?.mapKeyField || 'NOMCOMAR'"
            :data-key-field="widget.params?.dataKeyField || 'comarca'"
            :value-field="widget.params?.valueField || 'vehicles'"
            :title="tConfig(widget.params?.title, lang)"
            :subtitle="tConfig(widget.params?.subtitle, lang)"
            :lang="lang"
          />

          <TextWidget
            v-if="widget.type === 'text'"
            :title="tConfig(widget.params.title, lang)"
            :body="widget.params.body"
            :lang="lang"
          />

          <ListWidget
            v-if="widget.type === 'list'"
            :title="tConfig(widget.params.title, lang)"
            :columns="widget.params.columns"
            :data="chartData[widget.dataSourceKey]?.data || []"
            :lang="lang"
          />

          <PivotTableWidget
            v-if="widget.type === 'pivot'"
            :title="tConfig(widget.params.title?.text || widget.params.title, lang)"
            :data="chartData[widget.dataSourceKey]?.data || []"
            :row-field="widget.params.mapping?.category"
            :col-field="widget.params.mapping?.group"
            :value-field="widget.params.mapping?.value || 'count'"
            :row-label="tConfig(widget.params.rowLabel, lang)"
            :value-label="tConfig(widget.params.valueLabel, lang)"
          />

          <MatrixTableWidget
            v-if="widget.type === 'matrix-table'"
            :title="tConfig(widget.params.title?.text || widget.params.title, lang)"
            :data="chartData[widget.dataSourceKey]?.data || []"
            :row-field="widget.params.mapping?.category"
            :col-field="widget.params.mapping?.group"
            :value-field="widget.params.mapping?.value || 'count'"
            :row-label="tConfig(widget.params.rowLabel, lang)"
            :value-label="tConfig(widget.params.valueLabel, lang)"
          />
        </div>
      </div>
    </div>

    <PdfExportModal :visible="showPdfModal" :lang="lang" @export="exportToPDF" @close="showPdfModal = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import html2pdf from 'html2pdf.js';
import ChartWidget from '../components/ChartWidget.vue';
import MapWidget from '../components/MapWidget.vue';
import KpiWidget from '../components/KpiWidget.vue';
import TextWidget from '../components/TextWidget.vue';
import ListWidget from '../components/ListWidget.vue';
import PivotTableWidget from '../components/PivotTableWidget.vue';
import MatrixTableWidget from '../components/MatrixTableWidget.vue';
import PdfExportModal from '../components/PdfExportModal.vue';
import { getEffectiveConfig } from '../data/dashboardConfig';
import { dashboardService } from '../services/dashboardService';
import api, { setToken, setBaseUrl } from '../services/api';
import { t, tConfig } from '../i18n/index.js';
import { setDebug, debugLog } from '../utils/debug.js';

const props = defineProps({
  initialScreenId: { type: String, default: null },
  token: { type: String, default: null },
  showHeader: { type: Boolean, default: null },
  lang: { type: String, default: 'ca_es' },
  apiBaseUrl: { type: String, default: null },
  screens: { type: Array, default: null },
  editable: { type: Boolean, default: false },
  pdfExport: { type: Boolean, default: false },
  title: { type: [String, Object], default: null },
  subtitle: { type: [String, Object], default: null },
  debug: { type: Boolean, default: false }
});
const { initialScreenId } = props;

setToken(props.token || null);
setBaseUrl(props.apiBaseUrl || null);
setDebug(props.debug);

watch(() => props.debug, val => setDebug(val));

watch(() => props.token, (val) => { setToken(val || null); fetchData(); });
watch(() => props.apiBaseUrl, (val) => { setBaseUrl(val || null); });

defineEmits(['enter-config', 'open-widget-config']);

const FORMATTER_PRESETS = {
  millions: (val) => {
    if (val === undefined || val === null || isNaN(val)) return val;
    return (val / 1_000_000).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  },
  thousands: (val) => {
    if (val === undefined || val === null || isNaN(val)) return val;
    return (val / 1_000).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + 'K';
  },
  raw: (val) => val
};

const { screens: effectiveScreens } = getEffectiveConfig(props.screens);
const chartData = ref({});
const screens = ref(effectiveScreens);
const activeScreenId = ref(
  initialScreenId ||
  screens.value.find(s => s.active === true)?.id ||
  screens.value[0]?.id ||
  ''
);
const loading = ref(true);
const error = ref(null);
const metadataCache = ref({});
const headerConfig = ref({
  show_titles: props.showHeader !== null ? props.showHeader : true,
  editable: props.editable,
  pdf_export: props.pdfExport,
  title: props.title,
  subtitle: props.subtitle
});
const showPdfModal = ref(false);
const apiError = ref(null);
const crossFilters = ref({});

function detectApiError (err) {
  if (!api.defaults.baseURL) {
    apiError.value = t('errorNoBaseUrl', props.lang);
    return true;
  }
  const status = err?.response?.status;
  if (status === 401) {
    apiError.value = t('errorUnauthorized', props.lang);
    return true;
  }
  return false;
}

watch(() => props.showHeader, (val) => { if (val !== null) headerConfig.value.show_titles = val; });
watch(() => props.editable,  (val) => { headerConfig.value.editable = val; });
watch(() => props.pdfExport, (val) => { headerConfig.value.pdf_export = val; });
watch(() => props.title,     (val) => { headerConfig.value.title = val; });
watch(() => props.subtitle,  (val) => { headerConfig.value.subtitle = val; });

const activeScreen = computed(() => screens.value.find(s => s.id === activeScreenId.value) || screens.value[0]);
const kpiWidgets = computed(() => activeScreen.value?.widgets.filter(w => w.type === 'kpi' && w.active !== false) || []);
const otherWidgets = computed(() => activeScreen.value?.widgets.filter(w => w.type !== 'kpi' && w.active !== false) || []);
const isEditable = computed(() => headerConfig.value?.editable ?? true);
const isPdfExportEnabled = computed(() => headerConfig.value?.pdf_export ?? true);

watch(activeScreenId, () => fetchData());

onMounted(() => fetchData());

async function ensureSegmentMetadata(segmentId) {
  if (metadataCache.value[segmentId]) return metadataCache.value[segmentId];

  try {
    debugLog(`Discovering metadata for segment: ${segmentId}...`);
    const metadata = await dashboardService.getSegmentMetadata(segmentId);
    const rawList = metadata?.dimensionsList || metadata?.data?.dimensionsList || [];

    const dimMap = {};
    rawList.forEach(dim => {
      if (dim.indexField && dim.id) {
        const cleanKey = dim.indexField.replace(/[\[\]\" ]/g, '');
        dimMap[cleanKey.toLowerCase()] = dim.id;
      }
    });

    debugLog(`Metadata discovered for ${segmentId}:`, dimMap);
    metadataCache.value[segmentId] = dimMap;
    return dimMap;
  } catch (err) {
    detectApiError(err);
    return {};
  }
}

async function fetchData() {
  loading.value = true;
  error.value = null;
  apiError.value = null;

  if (!chartData.value) chartData.value = {};

  const widgets = activeScreen.value?.widgets || [];
  const segments = [...new Set(widgets.map(w => w.params?.segmentId).filter(id => !!id))];
  await Promise.all(segments.map(id => ensureSegmentMetadata(id)));

  const fetchPromises = widgets.map(async (widget) => {
    if (!widget.dataSourceKey || !widget.params?.segmentId) return;
    if (chartData.value[widget.dataSourceKey]) return;

    try {
      let jsonData = widget.params.jsonData;
      const dimensionParam = widget.params.dimension;
      const baseDimNames = Array.isArray(dimensionParam) ? dimensionParam : (dimensionParam ? [dimensionParam] : []);
      const activeFilter = crossFilters.value[widget.dataSourceKey];
      const filterFields = activeFilter ? Object.keys(activeFilter) : [];
      const dimensionNames = filterFields.length
        ? [...filterFields, ...baseDimNames.filter(d => !filterFields.includes(d))]
        : baseDimNames;

      if (dimensionNames.length > 0) {
        const dimMap = metadataCache.value[widget.params.segmentId] || {};
        const resolvedIds = dimensionNames
          .map(name => dimMap[name.toLowerCase().trim()])
          .filter(id => !!id);

        if (resolvedIds.length > 0) {
          jsonData = resolvedIds;
          debugLog(`Resolved dimensions [${dimensionNames}] to`, jsonData);
        }
      }

      const rawData = await dashboardService.getDashboardStats(widget.params.segmentId, jsonData);

      if (activeFilter && Array.isArray(rawData?.data)) {
        const dataKeys = rawData.data[0] ? Object.keys(rawData.data[0]) : [];
        rawData.data = rawData.data.filter(item => {
          return Object.entries(activeFilter).every(([field, value]) => {
            const matchKey = dataKeys.find(k => k.toLowerCase() === field.toLowerCase())
              || dataKeys.find(k => k.toLowerCase().includes(field.toLowerCase()))
              || field;
            return String(item[matchKey]) === String(value);
          });
        });
      }

      const transformedData = transformApiData(rawData, widget);
      chartData.value[widget.dataSourceKey] = transformedData;
    } catch (err) {
      detectApiError(err);
    }
  });

  try {
    await Promise.all(fetchPromises);
  } catch (err) {
    error.value = "Alguns gràfics no s'han pogut carregar.";
  } finally {
    loading.value = false;
  }
}

function getNestedProperty(obj, path) {
  if (!path || !obj) return undefined;
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value && typeof value === 'object') {
      value = value[key];
    } else {
      return undefined;
    }
  }
  return value;
}

function getTableCellValue(item, fieldPath) {
  if (!fieldPath || !item) return 'N/A';
  const value = getNestedProperty(item, fieldPath) || item[fieldPath];
  if (value === undefined || value === null) return 'N/A';
  if (typeof value === 'number') {
    return value.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return value;
}

function transformApiData(rawData, widget) {
  let list = Array.isArray(rawData) ? rawData : (rawData?.data || []);

  if (!Array.isArray(list) || list.length === 0) return { series: [], categories: [], labels: [] };

  const mapping = widget.params?.mapping;
  const valueKey = mapping?.value || 'count';

  // Resol un nom de mapping (ex. "indicators[indicatorId=...].valueDescLanguage")
  // a la clau real que retorna l'API (que pot tenir [],"," eliminats o substituits).
  const resolveProp = (mappingName, keys) => {
    if (!mappingName || !keys || keys.length === 0) return null;
    const normalize = (s) => s.toLowerCase().replace(/[\[\]"= _.]/g, '');
    const targetNorm = normalize(mappingName);
    // 1. Coincidència exacta
    if (keys.includes(mappingName)) return mappingName;
    // 2. Coincidència exacta normalitzada (separadors diferents però mateix nom)
    let prop = keys.find(k => normalize(k) === targetNorm);
    if (prop) return prop;
    // 3. Inclusió amb límit de paraula (evita que "comarca" matcheji "comarcamap")
    prop = keys.find(k => {
      const kn = normalize(k);
      if (!kn.includes(targetNorm)) return false;
      const idx = kn.indexOf(targetNorm);
      const after = kn[idx + targetNorm.length];
      // Acceptar només si el match arriba al final o segueix amb un separador
      return after === undefined;
    });
    if (prop) return prop;
    // 4. Inclusió inversa (mappingName conté la clau de l'API tal qual)
    prop = keys.find(k => {
      const kn = normalize(k);
      return kn.length >= 3 && targetNorm.includes(kn);
    });
    if (prop) return prop;
    // 5. Fallback heurístic per a indicadors amb format complex
    return keys.find(k =>
      k.includes('valueDesc') ||
      k.includes('indicator') ||
      k.toLowerCase().includes('description')
    ) || null;
  };

  const dataKeys = list.length > 0 ? Object.keys(list[0]) : [];
  const categoryProp = resolveProp(mapping?.category, dataKeys);
  const groupProp = resolveProp(mapping?.group, dataKeys);

  debugLog('Widget:', widget.id, '| Keys:', dataKeys, '| CategoryProp:', categoryProp, '| GroupProp:', groupProp);

  // Crear categoryLabel a partir de la propietat detectada
  if (list.length > 0 && categoryProp) {
    list = list.map(item => ({
      ...item,
      categoryLabel: item[categoryProp] || 'N/A'
    }));
  }

  if (widget.type === 'kpi') {
    const total = list.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
    return { [widget.params.valueKey || 'total']: total };
  }

  if (widget.type === 'pivot' || widget.type === 'matrix-table') {
    const valueProp = resolveProp(mapping?.value, dataKeys) || mapping?.value;
    const rows = list.map(item => {
      const row = { ...item };
      if (mapping?.category && categoryProp && categoryProp !== mapping.category) {
        row[mapping.category] = item[categoryProp];
      }
      if (mapping?.group && groupProp && groupProp !== mapping.group) {
        row[mapping.group] = item[groupProp];
      }
      if (mapping?.value && valueProp && valueProp !== mapping.value) {
        row[mapping.value] = item[valueProp];
      }
      return row;
    });
    return { data: rows };
  }

  if (mapping?.category && mapping?.group) {
    const categoryKey = categoryProp || mapping.category;
    const groupKey = groupProp || mapping.group;

    let categories = [...new Set(list.map(item => String(item[categoryKey] ?? 'N/A')))];
    const groups = [...new Set(list.map(item => String(item[groupKey] ?? 'N/A')))].sort();

    const sortBy = widget.params?.sortBy || 'category';
    if (sortBy === 'total') {
      const totals = {};
      list.forEach(item => {
        const cat = String(item[categoryKey] ?? 'N/A');
        totals[cat] = (totals[cat] || 0) + (Number(item[valueKey]) || 0);
      });
      categories.sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
    } else {
      categories.sort((a, b) => {
        const aNum = Number(a);
        const bNum = Number(b);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return String(a).localeCompare(String(b), 'ca-ES');
      });
    }

    const series = groups.map(groupName => ({
      name: groupName,
      data: categories.map(catName => {
        const match = list.find(item => String(item[categoryKey]) === catName && String(item[groupKey]) === groupName);
        return match ? Number(match[valueKey]) || 0 : 0;
      })
    }));

    const displayCategories = categories.map(cat => {
      const num = Number(cat);
      if (!isNaN(num) && cat.includes('.')) return Math.floor(num).toString();
      return cat;
    });

    return { series, categories: displayCategories };
  }

  if (widget.params?.chart?.type === 'table') {
    const valueProp = resolveProp(mapping?.value, dataKeys) || mapping?.value;
    const rows = list.map(item => {
      const row = { ...item };
      if (mapping?.category && categoryProp && categoryProp !== mapping.category) {
        row[mapping.category] = item[categoryProp];
      }
      if (mapping?.value && valueProp && valueProp !== mapping.value) {
        row[mapping.value] = item[valueProp];
      }
      return row;
    });
    return { data: rows };
  }

  if (widget.type === 'map') {
    const dataKeyField = widget.params?.dataKeyField;
    const valueField = widget.params?.valueField || 'count';
    const mapKeyProp = resolveProp(dataKeyField, dataKeys);
    const valueProp = resolveProp(valueField, dataKeys) || valueField;
    const rows = list.map(item => {
      const row = { ...item };
      if (dataKeyField && mapKeyProp && mapKeyProp !== dataKeyField) {
        row[dataKeyField] = item[mapKeyProp];
      }
      if (valueField && valueProp && valueProp !== valueField) {
        row[valueField] = item[valueProp];
      }
      return row;
    });
    return { data: rows };
  }

  const categoryKey = mapping?.category || mapping?.label || 'categoryLabel' || rawData?.currentDimensionField || 'marca';

  // Crear array de { categoria, valor }
  const dataPoints = list.map(item => {
    let category = item.categoryLabel;
    if (!category) {
      const nestedValue = getNestedProperty(item, categoryKey);
      category = nestedValue || item[categoryKey] || 'N/A';
    }
    const value = Number(item[valueKey]) || 0;
    return { category, value };
  });

  // Ordenar segons la paramètrica (per defecte: per value)
  const sortBy = widget.params?.sortBy || 'value';
  if (sortBy === 'value') {
    dataPoints.sort((a, b) => b.value - a.value);
  } else {
    // Ordenar per categoria (numèricament si són números, sinó alfabèticament)
    dataPoints.sort((a, b) => {
      const aNum = Number(a.category);
      const bNum = Number(b.category);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return String(a.category).localeCompare(String(b.category), 'ca-ES');
    });
  }

  const labels = dataPoints.map(p => {
    const cat = String(p.category);
    const num = Number(cat);
    // Si és número amb decimals, eliminar-los
    if (!isNaN(num) && cat.includes('.')) {
      return Math.floor(num).toString();
    }
    return cat;
  });
  const values = dataPoints.map(p => p.value);

  if (widget.params?.chart?.type === 'donut' || widget.params?.chart?.type === 'pie') {
    return { series: values, labels };
  }

  return {
    series: [{ name: tConfig(widget.params?.title?.text || widget.params?.title, props.lang) || 'Total', data: values }],
    categories: labels
  };
}

function getMergedOptions(widget) {
  const merged = JSON.parse(JSON.stringify(widget.params || {}));

  const resolveFormatter = (f) => {
    if (typeof f !== 'string') return f;
    return FORMATTER_PRESETS[f] ?? undefined;
  };
  if (merged.xaxis?.labels) merged.xaxis.labels.formatter = resolveFormatter(merged.xaxis.labels.formatter);
  if (merged.yaxis?.labels) merged.yaxis.labels.formatter = resolveFormatter(merged.yaxis.labels.formatter);

  if (merged.title?.text) merged.title.text = tConfig(merged.title.text, props.lang);
  if (merged.yaxis?.title?.text) merged.yaxis.title.text = tConfig(merged.yaxis.title.text, props.lang);
  if (merged.xaxis?.title?.text) merged.xaxis.title.text = tConfig(merged.xaxis.title.text, props.lang);

  const dataNode = chartData.value[widget.dataSourceKey];

  if (dataNode) {
    if (dataNode.categories) {
      if (!merged.xaxis) merged.xaxis = {};
      merged.xaxis.categories = dataNode.categories;
    }
    if (dataNode.labels) {
      merged.labels = dataNode.labels;
    }
  }

  const cf = widget.params?.crossfilter;
  if (cf) {
    const activeValue = crossFilters.value[cf.target]?.[cf.field];
    if (activeValue !== undefined) {
      const categories = dataNode?.categories || dataNode?.labels || [];
      const baseColor = (widget.params.colors && widget.params.colors[0]) || '#0b5394';
      const dimColor = '#d1d5db';
      merged.colors = categories.map(c => String(c) === String(activeValue) ? baseColor : dimColor);
      if (!merged.plotOptions) merged.plotOptions = {};
      if (!merged.plotOptions.bar) merged.plotOptions.bar = {};
      merged.plotOptions.bar.distributed = true;
      if (!merged.legend) merged.legend = {};
      merged.legend.show = false;
    }
  }

  const numberFormatter = {
    formatter: (val) => {
      if (val === undefined || val === null || isNaN(val)) return val;

      if (val % 1 !== 0) {
        const formatted = Number(val).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        return formatted;
      }
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  };

  // Per gràfics pie/donut, el tooltip NO ha d'afegir % (són números raw, no percentatges)
  const tooltipFormatter = {
    formatter: (val) => {
      if (val === undefined || val === null || isNaN(val)) return val;
      if (val % 1 !== 0) {
        return Number(val).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  };

  if (!merged.dataLabels) merged.dataLabels = { formatter: numberFormatter.formatter, enabled: true };
  else if (!merged.dataLabels.formatter) merged.dataLabels.formatter = numberFormatter.formatter;

  if (!merged.yaxis) merged.yaxis = { labels: { formatter: numberFormatter.formatter } };
  else if (!merged.yaxis.labels) merged.yaxis.labels = { formatter: numberFormatter.formatter };
  else if (!merged.yaxis.labels.formatter) merged.yaxis.labels.formatter = numberFormatter.formatter;

  if (!merged.xaxis) merged.xaxis = { labels: { formatter: numberFormatter.formatter } };
  else if (!merged.xaxis.labels) merged.xaxis.labels = { formatter: numberFormatter.formatter };
  else if (!merged.xaxis.labels.formatter) merged.xaxis.labels.formatter = numberFormatter.formatter;

  if (!merged.tooltip) merged.tooltip = { y: { formatter: tooltipFormatter.formatter } };
  else if (!merged.tooltip.y) merged.tooltip.y = { formatter: tooltipFormatter.formatter };
  else if (!merged.tooltip.y.formatter) merged.tooltip.y.formatter = tooltipFormatter.formatter;

  return merged;
}

function handleChartClick(widget, config) {
  if (!config || !widget?.params?.crossfilter) return;
  const cf = widget.params.crossfilter;
  const data = chartData.value[widget.dataSourceKey];
  const value = data?.categories?.[config.dataPointIndex] || data?.labels?.[config.dataPointIndex];
  if (value === undefined || value === null) return;

  const current = crossFilters.value[cf.target]?.[cf.field];
  if (current === String(value)) {
    delete crossFilters.value[cf.target];
  } else {
    crossFilters.value[cf.target] = { [cf.field]: String(value) };
  }

  delete chartData.value[cf.target];
  fetchData();
}

function openPdfExportModal() {
  showPdfModal.value = true;
}

async function exportToPDF(exportOptions) {
  showPdfModal.value = false;

  // Esperar un tick perquè el modal desaparegui
  await new Promise(resolve => setTimeout(resolve, 200));

  // Guardar elements a amagar temporalment
  const header = document.querySelector('.dashboard-header');
  const configBtns = document.querySelectorAll('.widget-config-btn');
  const widgetHeaders = document.querySelectorAll('.widget-header');
  const apexToolbars = document.querySelectorAll('.apexcharts-toolbar');

  // Amagar capçalera
  if (header) header.style.display = 'none';

  // Amagar botons de configuració
  configBtns.forEach(btn => btn.style.display = 'none');

  // Amagar botons "View Table" / controls
  if (exportOptions.hideButtons) {
    widgetHeaders.forEach(el => el.style.display = 'none');
    apexToolbars.forEach(el => el.style.display = 'none');
  }

  // Amagar KPIs si no es vol
  const kpiGrid = document.querySelector('.kpi-grid');
  if (!exportOptions.includeKpi && kpiGrid) {
    kpiGrid.style.display = 'none';
  }

  // Capturar l'element VIVA del dashboard (no un clon)
  const element = document.querySelector('.dashboard-content');
  if (!element) {
    alert("No s'ha pogut trobar el contingut del dashboard");
    // Restaurar tot
    if (header) header.style.display = '';
    configBtns.forEach(btn => btn.style.display = '');
    widgetHeaders.forEach(el => el.style.display = '');
    apexToolbars.forEach(el => el.style.display = '');
    if (kpiGrid) kpiGrid.style.display = '';
    return;
  }

  // Esperar un moment perquè el DOM s'actualitzi
  await new Promise(resolve => setTimeout(resolve, 500));

  const qualitySettings = {
    low: { scale: 2, quality: 0.75 },
    medium: { scale: 2.5, quality: 0.88 },
    high: { scale: 3, quality: 0.98 }
  };

  const quality = qualitySettings[exportOptions.quality] || qualitySettings.high;
  const fileName = `Dashboard_${activeScreen.value?.title || 'Export'}_${new Date().toISOString().split('T')[0]}.pdf`;

  const options = {
    margin: [10, 10, 10, 10],
    filename: fileName,
    image: { type: 'jpeg', quality: quality.quality },
    html2canvas: {
      scale: quality.scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#f3f4f6',
      scrollX: 0,
      scrollY: 0,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    },
    jsPDF: {
      orientation: exportOptions.orientation,
      unit: 'mm',
      format: 'a4',
      compress: true
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  try {
    await html2pdf().set(options).from(element).save();
  } catch (err) {
    console.error('Error exportant PDF:', err);
    alert("Hi ha hagut un error exportant el PDF");
  }

  // Restaurar tot
  if (header) header.style.display = '';
  configBtns.forEach(btn => btn.style.display = '');
  if (exportOptions.hideButtons) {
    widgetHeaders.forEach(el => el.style.display = '');
    apexToolbars.forEach(el => el.style.display = '');
  }
  if (!exportOptions.includeKpi && kpiGrid) {
    kpiGrid.style.display = '';
  }
}
</script>

<style scoped>
.dashboard-container {
  margin: 0;
  overflow-x: hidden;
  font-family: 'Source Sans Pro', sans-serif;
  background-color: transparent;
  padding: 0;
}

.dashboard-header {
  margin-bottom: 0;
  padding: 0 0 10px 0;
}

.dashboard-header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  padding: 3px 10px;
  margin-bottom: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  line-height: normal;
}

.dashboard-header p {
  color: #666;
  font-size: 17px;
  padding: 3px 10px;
  font-family: 'Source Sans Pro', sans-serif;
  line-height: normal;
  margin-bottom: 2px;
}

.kpi-grid {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .kpi-grid {
    justify-content: center;
  }
}

.kpi-grid > * {
  flex: 0 1 180px;
}

.grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 1200px) {
  .grid-layout {
    grid-template-columns: repeat(6, 1fr);
  }

  .full-width      { grid-column: span 6; }
  .half-width      { grid-column: span 3; }
  .third-width     { grid-column: span 2; }
  .two-thirds-width { grid-column: span 4; }

  .grid-layout > :not(.full-width):not(.half-width):not(.third-width):not(.two-thirds-width) {
    grid-column: span 3;
  }
}

.tabs-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0;
  margin-top: 0;
  border-bottom: 1px dotted #bbbbbb;
  padding-bottom: 0;
  background-color: #efefef;
  max-height: 35px;
  height: 35px;
}

.tab-button {
  background: transparent;
  border: 0;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 15px;
  font-weight: 600;
  text-transform: none;
  color: #000;
  padding: 7px 9px 8px 10px;
  cursor: pointer;
  position: relative;
  line-height: normal;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  transition: text-decoration-color 0.2s ease-in;
  border-radius: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-button:hover {
  text-decoration-color: rgb(212, 46, 18);
}

.tab-button.active {
  text-decoration-color: rgb(212, 46, 18);
}

.tab-button.active::after {
  display: none;
}

.pdf-btn,
.config-btn {
  background: none;
  border: 1px solid var(--sherpa-border, #ddd);
  border-radius: 3px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--sherpa-muted, #888);
  transition: all 0.2s;
  margin-left: auto;
}

.pdf-btn { margin-right: 4px; }

.pdf-btn:hover   { border-color: var(--sherpa-teal, #21B7C9); color: var(--sherpa-teal, #21B7C9); }
.config-btn:hover { border-color: var(--sherpa-red, #d42e12); color: var(--sherpa-red, #d42e12); }

.widget-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.widget-wrapper > *:not(.widget-config-btn) {
  flex: 1;
  min-height: 0;
}

.widget-config-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 20;
  background: rgba(212, 46, 18, 0.85);
  color: white;
  border: none;
  border-radius: 3px;
  padding: 3px 7px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-wrapper:hover .widget-config-btn,
.kpi-card-wrapper:hover .widget-config-btn {
  opacity: 1;
}

.widget-config-btn:hover {
  background: #d42e12;
}

@media (max-width: 768px) {
  .widget-config-btn { opacity: 0.8; }
  .grid-layout { grid-template-columns: 1fr; }
}

.api-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #664d03;
}

.api-error-icon { font-size: 18px; flex-shrink: 0; }
.api-error-msg  { line-height: 1.4; }

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
}

@media print {
  .dashboard-header {
    position: static;
    background: white;
    padding: 20px;
    margin-bottom: 20px;
  }

  .tabs-container {
    display: none;
  }

  .pdf-btn,
  .config-btn,
  .widget-config-btn {
    display: none !important;
  }

  .dashboard-container {
    background: white;
  }

  .grid-layout,
  .kpi-grid {
    gap: 20px;
  }

  .widget-wrapper {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  apexchart {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  body {
    margin: 0;
    padding: 0;
  }
}

.table-widget {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
}

.table-widget .data-table {
  table-layout: fixed;
  word-wrap: break-word;
}

.table-title {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  position: sticky;
  top: 0;
}

.data-table tr:hover {
  background-color: #f9fafb;
}
</style>
